import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Settings, 
  X, 
  RefreshCw, 
  Database, 
  Download, 
  Upload,
  Trash2,
  Shield,
  HardDrive,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Clock,
  Info,
  ChevronDown,
  ChevronRight,
  Smartphone,
  Monitor,
  Zap,
  ArrowUpCircle,
  ExternalLink,
  Globe
} from 'lucide-react';
import { syncManager, SyncStatus } from '../services/syncManager';
import { pwaDataManager } from '../utils/pwaDataManager';
import { useEnhancedPWAInstall } from '../hooks/useEnhancedPWAInstall';
import { versionManager } from '../utils/versionManager';
import { testPWAInstallation, testVersionInfo } from '../utils/pwaInstallTest';
import { testPWAInstallationComprehensive, quickPWATest } from '../utils/pwaInstallationTest';
import { toast } from 'react-hot-toast';
import Button from './Button';
import PWAInstallInstructions from './PWAInstallInstructions';
import BaseUrlConfig from './BaseUrlConfig';

const SettingsContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 16px;
  box-shadow: ${({ theme }) => theme.shadows.xl};
  z-index: 3000;
  max-width: 400px;
  max-height: 80vh;
  overflow-y: auto;
  transform: ${({ $isOpen }) => $isOpen ? 'translateY(0)' : 'translateY(100%)'};
  opacity: ${({ $isOpen }) => $isOpen ? 1 : 0};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
  
  @media (max-width: 768px) {
    bottom: 10px;
    right: 10px;
    left: 10px;
    max-width: none;
    max-height: 90vh;
  }
`;

const SettingsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.colors.background};
  border-radius: 16px 16px 0 0;
`;

const SettingsTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 18px;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme }) => theme.colors.backgroundSecondary};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const SettingsContent = styled.div`
  padding: 20px;
`;

const Section = styled.div`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: ${({ theme }) => theme.colors.backgroundSecondary};
  }
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const SectionContent = styled.div<{ $isExpanded: boolean }>`
  max-height: ${({ $isExpanded }) => $isExpanded ? '500px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  margin-left: 32px;
`;

const StatusItem = styled.div<{ $status: 'healthy' | 'warning' | 'error' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  margin-bottom: 8px;
  font-size: 14px;
  
  ${({ $status }) => {
    switch ($status) {
      case 'healthy':
        return `
          background: rgba(34, 197, 94, 0.1);
          color: #16a34a;
          border: 1px solid rgba(34, 197, 94, 0.2);
        `;
      case 'warning':
        return `
          background: rgba(245, 158, 11, 0.1);
          color: #d97706;
          border: 1px solid rgba(245, 158, 11, 0.2);
        `;
      case 'error':
        return `
          background: rgba(239, 68, 68, 0.1);
          color: #dc2626;
          border: 1px solid rgba(239, 68, 68, 0.2);
        `;
    }
  }}
`;

const StatusIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
`;

const StatusText = styled.div`
  flex: 1;
  font-weight: 500;
`;

const StatusDetails = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 2px;
`;

const ActionButton = styled(Button)`
  font-size: 13px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
  margin-bottom: 8px;
  width: 100%;
`;

const BackupList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  margin-bottom: 12px;
`;

const BackupItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  margin-bottom: 6px;
  font-size: 13px;
`;

const BackupInfo = styled.div`
  flex: 1;
`;

const BackupName = styled.div`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const BackupDate = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 2px;
`;

const BackupActions = styled.div`
  display: flex;
  gap: 4px;
`;

const BackupActionButton = styled.button<{ $variant: 'restore' | 'delete' }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  ${({ $variant }) => {
    switch ($variant) {
      case 'restore':
        return `
          background: rgba(34, 197, 94, 0.1);
          color: #16a34a;
          
          &:hover {
            background: rgba(34, 197, 94, 0.2);
          }
        `;
      case 'delete':
        return `
          background: rgba(239, 68, 68, 0.1);
          color: #dc2626;
          
          &:hover {
            background: rgba(239, 68, 68, 0.2);
          }
        `;
    }
  }}
`;

const ConfirmationDialog = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4000;
  opacity: ${({ $isVisible }) => $isVisible ? 1 : 0};
  visibility: ${({ $isVisible }) => $isVisible ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const ConfirmationContent = styled.div`
  background: ${({ theme }) => theme.colors.background};
  border-radius: 12px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  box-shadow: ${({ theme }) => theme.shadows.xl};
`;

const ConfirmationTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ConfirmationMessage = styled.p`
  margin: 0 0 20px 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
`;

const ConfirmationButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const ConfirmationButton = styled(Button)<{ $variant: 'primary' | 'secondary' | 'danger' }>`
  padding: 8px 16px;
  font-size: 14px;
`;

interface BackupVersion {
  id: string;
  name: string;
  timestamp: Date;
  size: number;
  description: string;
}

interface AppSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

const AppSettings: React.FC<AppSettingsProps> = ({ isOpen, onClose }) => {
  const { 
    isPWAMode, 
    isBrowserMode, 
    displayMode, 
    installSupported, 
    isInstalled,
    canInstall,
    isIOSDevice,
    isAndroidDevice,
    isMobile,
    isIncognito,
    userEngagement,
    handleInstallClick,
    triggerInstallPrompt,
    triggerMobileInstall
  } = useEnhancedPWAInstall();
  
  const [syncStatus, setSyncStatus] = useState<SyncStatus>(syncManager.getSyncStatus());
  const [isLoading, setIsLoading] = useState(false);
  const [storageInfo, setStorageInfo] = useState<StorageEstimate | null>(null);
  const [isPersisted, setIsPersisted] = useState<boolean | null>(null);
  const [backupVersions, setBackupVersions] = useState<BackupVersion[]>([]);
  const [updateInfo, setUpdateInfo] = useState<any>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateProgress, setUpdateProgress] = useState(0);
  const [showInstallInstructions, setShowInstallInstructions] = useState(false);
  
  // Section visibility
  const [expandedSections, setExpandedSections] = useState({
    sync: true,
    health: false,
    backup: false,
    baseurl: false,
    install: false,
    updates: false
  });
  
  // Confirmation dialogs
  const [confirmation, setConfirmation] = useState<{
    isVisible: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type: 'backup' | 'restore' | 'delete';
    backupVersion?: BackupVersion;
  }>({
    isVisible: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'backup'
  });

  useEffect(() => {
    if (isOpen) {
      loadData();
      checkForUpdates();
      // Refresh version information
      versionManager.refreshVersion();
    }
  }, [isOpen]);

  useEffect(() => {
    const unsubscribe = syncManager.onSyncStatusChange(setSyncStatus);
    return unsubscribe;
  }, []);

  const loadData = async () => {
    try {
      const info = await pwaDataManager.getStorageInfo();
      setStorageInfo(info);

      const persisted = await navigator.storage?.persisted();
      setIsPersisted(persisted);

      // Load backup versions
      await loadBackupVersions();
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const loadBackupVersions = async () => {
    try {
      const versions = await pwaDataManager.getBackupVersions();
      setBackupVersions(versions);
    } catch (error) {
      console.error('Error loading backup versions:', error);
    }
  };

  const checkForUpdates = async () => {
    try {
      const updateCheck = await versionManager.checkForUpdates();
      if (updateCheck.hasUpdate) {
        setUpdateInfo(updateCheck);
      }
    } catch (error) {
      console.error('Error checking for updates:', error);
    }
  };

  const handleUpdate = async () => {
    if (!updateInfo) return;

    setIsUpdating(true);
    setUpdateProgress(0);

    try {
      // Simulate update progress
      const progressInterval = setInterval(() => {
        setUpdateProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // Trigger service worker update
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
      }

      // Wait a bit for the update to process
      await new Promise(resolve => setTimeout(resolve, 1000));

      setUpdateProgress(100);
      
      // Reload the page to apply the update
      setTimeout(() => {
        window.location.reload();
      }, 500);

      toast.success('App updated successfully!');
    } catch (error) {
      console.error('Error updating app:', error);
      toast.error('Failed to update app');
    } finally {
      setIsUpdating(false);
      setUpdateProgress(0);
    }
  };

  const handleInstall = async () => {
    try {
      console.log('🚀 Attempting to install PWA...', {
        canInstall,
        isInstalled,
        isIncognito,
        isPWAMode: isPWAMode(),
        isBrowserMode: isBrowserMode(),
        userEngagement
      });
      
      // Force user engagement first
      if (!userEngagement) {
        console.log('🔧 Forcing user engagement...');
        (window as any).userGesture = true;
        (window as any).forceUserEngagement?.();
      }
      
      // Try multiple installation methods
      let success = false;
      
      // Method 1: Try enhanced install click
      console.log('🔄 Method 1: Enhanced install click');
      success = await handleInstallClick();
      
      if (success) {
        console.log('✅ PWA installation successful via Method 1');
        toast.success('App installed successfully!');
        window.location.reload();
        return;
      }
      
      // Method 2: Try global trigger function
      console.log('🔄 Method 2: Global trigger function');
      if ((window as any).triggerPWAInstall) {
        const result = await (window as any).triggerPWAInstall();
        if (result.success) {
          console.log('✅ PWA installation successful via Method 2');
          toast.success('App installed successfully!');
          window.location.reload();
          return;
        }
      }
      
      // Method 3: Try to trigger beforeinstallprompt manually
      console.log('🔄 Method 3: Manual beforeinstallprompt trigger');
      const beforeInstallEvent = new Event('beforeinstallprompt', { bubbles: true });
      window.dispatchEvent(beforeInstallEvent);
      
      // Wait and try again
      await new Promise(resolve => setTimeout(resolve, 1000));
      success = await handleInstallClick();
      
      if (success) {
        console.log('✅ PWA installation successful via Method 3');
        toast.success('App installed successfully!');
        window.location.reload();
        return;
      }
      
      // Method 4: Try mobile-specific installation
      if (isMobile) {
        console.log('🔄 Method 4: Mobile-specific installation');
        await triggerMobileInstall();
        success = await handleInstallClick();
        
        if (success) {
          console.log('✅ PWA installation successful via Method 4');
          toast.success('App installed successfully!');
          window.location.reload();
          return;
        }
      }
      
      // All methods failed, show instructions
      console.log('❌ All installation methods failed, showing instructions');
      toast.error('Native installation not available - showing manual instructions');
      setShowInstallInstructions(true);
      
    } catch (error) {
      console.error('❌ Error installing app:', error);
      toast.error('Installation failed - showing manual instructions');
      setShowInstallInstructions(true);
    }
  };

  const handleShowInstructions = () => {
    setShowInstallInstructions(true);
  };

  const handleDebugInstall = () => {
    console.log('=== Debug PWA Installation ===');
    const testResults = testPWAInstallation();
    console.log('Test Results:', testResults);
    toast.info('PWA installation debug info logged to console');
  };

  const handleComprehensiveTest = async () => {
    try {
      console.log('🧪 Running comprehensive PWA installation test...');
      const results = await testPWAInstallationComprehensive();
      console.log('📊 Comprehensive Test Results:', results);
      toast.success('Comprehensive PWA test completed - check console for details');
    } catch (error) {
      console.error('❌ Comprehensive test failed:', error);
      toast.error('Comprehensive test failed - check console for errors');
    }
  };

  const handleQuickTest = () => {
    console.log('⚡ Running quick PWA test...');
    const results = quickPWATest();
    console.log('⚡ Quick Test Results:', results);
    toast.info('Quick PWA test completed - check console for results');
  };

  const handleDebugVersion = () => {
    console.log('=== Debug Version Information ===');
    const versionResults = testVersionInfo();
    console.log('Version Results:', versionResults);
    console.log('Version Manager:', {
      currentVersion: versionManager.getCurrentVersion(),
      currentBuild: versionManager.getCurrentBuild(),
      versionInfo: versionManager.getVersionInfo()
    });
    toast.info('Version debug info logged to console');
  };

  const handleMobileInstall = async () => {
    try {
      console.log('Attempting mobile-specific installation...');
      await triggerMobileInstall();
      
      // Also try the regular install
      const success = await handleInstallClick();
      
      if (success) {
        console.log('Mobile PWA installation successful');
        toast.success('App installed successfully!');
        window.location.reload();
      } else {
        console.log('Mobile install not available, showing instructions');
        setShowInstallInstructions(true);
      }
    } catch (error) {
      console.error('Error with mobile installation:', error);
      toast.error('Mobile installation failed - showing manual instructions');
      setShowInstallInstructions(true);
    }
  };

  const handleForceInstall = async () => {
    try {
      console.log('🚀 FORCE INSTALL: Attempting all installation methods...');
      
      // Force user engagement
      (window as any).userGesture = true;
      (window as any).forceUserEngagement?.();
      
      // Method 1: Try all the regular methods
      await handleInstall();
      
      // Method 2: Try to create multiple temporary buttons and click them
      console.log('🔄 FORCE: Creating multiple temp buttons...');
      for (let i = 0; i < 3; i++) {
        const tempButton = document.createElement('button');
        tempButton.style.position = 'fixed';
        tempButton.style.top = '-1000px';
        tempButton.style.left = '-1000px';
        tempButton.style.width = '1px';
        tempButton.style.height = '1px';
        tempButton.style.opacity = '0';
        tempButton.textContent = 'Install App';
        document.body.appendChild(tempButton);
        
        // Add event listener
        const handler = (e) => {
          e.preventDefault();
          (window as any).deferredPrompt = e;
          console.log('🔔 FORCE: Captured prompt from temp button', i);
        };
        
        window.addEventListener('beforeinstallprompt', handler, { once: true });
        
        // Click and wait
        tempButton.click();
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Try to use the prompt
        if ((window as any).deferredPrompt) {
          try {
            await (window as any).deferredPrompt.prompt();
            const { outcome } = await (window as any).deferredPrompt.userChoice;
            if (outcome === 'accepted') {
              console.log('✅ FORCE: Installation successful via temp button', i);
              toast.success('App installed successfully!');
              window.location.reload();
              return;
            }
          } catch (error) {
            console.error('❌ FORCE: Error with temp button', i, error);
          }
        }
        
        // Clean up
        document.body.removeChild(tempButton);
        window.removeEventListener('beforeinstallprompt', handler);
      }
      
      // Method 3: Try to dispatch multiple events
      console.log('🔄 FORCE: Dispatching multiple events...');
      for (let i = 0; i < 5; i++) {
        const event = new Event('beforeinstallprompt', { bubbles: true });
        window.dispatchEvent(event);
        await new Promise(resolve => setTimeout(resolve, 200));
      }
      
      // Method 4: Try to trigger through user gesture simulation
      console.log('🔄 FORCE: Simulating user gestures...');
      const gestures = ['click', 'touchstart', 'mousedown', 'keydown'];
      for (const gesture of gestures) {
        const event = new Event(gesture, { bubbles: true });
        document.dispatchEvent(event);
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Final attempt with regular install
      const success = await handleInstallClick();
      if (success) {
        console.log('✅ FORCE: Installation successful via final attempt');
        toast.success('App installed successfully!');
        window.location.reload();
      } else {
        console.log('❌ FORCE: All methods failed');
        toast.error('Force installation failed - showing manual instructions');
        setShowInstallInstructions(true);
      }
      
    } catch (error) {
      console.error('❌ FORCE: Error during force install:', error);
      toast.error('Force installation failed - showing manual instructions');
      setShowInstallInstructions(true);
    }
  };

  const handleForceRefresh = async () => {
    try {
      setIsLoading(true);
      console.log('🔄 Force refreshing build files...');
      
      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log('✅ All caches cleared');
      }
      
      // Clear service worker cache
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
        console.log('✅ Service worker cache cleared');
      }
      
      // Force refresh version info
      await versionManager.refreshVersion();
      console.log('✅ Version info refreshed');
      
      // Clear localStorage version cache
      localStorage.removeItem('app_version');
      localStorage.removeItem('app_build');
      localStorage.removeItem('version_cache');
      console.log('✅ Version cache cleared');
      
      // Reload the page to fetch fresh files
      toast.success('Build files refreshed! Reloading...');
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      
    } catch (error) {
      console.error('❌ Error force refreshing:', error);
      toast.error('Failed to refresh build files');
    } finally {
      setIsLoading(false);
    }
  };

  const getUpdateIcon = (type: string) => {
    switch (type) {
      case 'major': return <AlertTriangle size={16} />;
      case 'minor': return <Zap size={16} />;
      case 'patch': return <CheckCircle size={16} />;
      case 'build': return <RefreshCw size={16} />;
      default: return <Info size={16} />;
    }
  };

  const getUpdateTitle = (type: string) => {
    switch (type) {
      case 'major': return 'Major Update Available';
      case 'minor': return 'New Features Available';
      case 'patch': return 'Update Available';
      case 'build': return 'New Build Available';
      default: return 'Update Available';
    }
  };

  const getUpdateDescription = (type: string, version: string, build: number) => {
    const currentVersion = versionManager.getCurrentVersion();
    const currentBuild = versionManager.getCurrentBuild();
    
    switch (type) {
      case 'major': return `A major update (${version} Build ${build}) is available with significant improvements and new features. Current: ${currentVersion} Build ${currentBuild}`;
      case 'minor': return `New features and improvements are available in version ${version} Build ${build}. Current: ${currentVersion} Build ${currentBuild}`;
      case 'patch': return `Bug fixes and improvements are available in version ${version} Build ${build}. Current: ${currentVersion} Build ${currentBuild}`;
      case 'build': return `A new build (#${build}) is available with the latest changes. Current: ${currentVersion} Build ${currentBuild}`;
      default: return `Version ${version} Build ${build} is available. Current: ${currentVersion} Build ${currentBuild}`;
    }
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const showConfirmation = (
    title: string, 
    message: string, 
    onConfirm: () => void, 
    type: 'backup' | 'restore' | 'delete',
    backupVersion?: BackupVersion
  ) => {
    setConfirmation({
      isVisible: true,
      title,
      message,
      onConfirm,
      type,
      backupVersion
    });
  };

  const hideConfirmation = () => {
    setConfirmation(prev => ({ ...prev, isVisible: false }));
  };

  const handleForceSync = async () => {
    try {
      await syncManager.forceSync();
      toast.success('Sync initiated');
    } catch (error) {
      toast.error('Failed to initiate sync');
    }
  };

  const handleBackupData = () => {
    const timestamp = new Date().toLocaleString();
    showConfirmation(
      'Create Data Backup',
      `Are you sure you want to create a backup of your app data? This will save your current settings, preferences, and offline data as of ${timestamp}. This backup will be stored locally and can be restored later.`,
      async () => {
        setIsLoading(true);
        try {
          const result = await pwaDataManager.createVersionedBackup(
            `Backup ${new Date().toLocaleDateString()}`,
            `Manual backup created on ${timestamp}`
          );
          if (result.success) {
            toast.success('Data backed up successfully!');
            await loadBackupVersions();
          } else {
            toast.error('Failed to create backup');
          }
        } catch (error) {
          console.error('Error backing up data:', error);
          toast.error('Failed to backup data');
        } finally {
          setIsLoading(false);
        }
        hideConfirmation();
      },
      'backup'
    );
  };

  const handleRestoreData = (backupVersion: BackupVersion) => {
    showConfirmation(
      'Restore Data Backup',
      `Are you sure you want to restore data from "${backupVersion.name}" created on ${backupVersion.timestamp.toLocaleString()}? This will replace your current data and cannot be undone. All current settings and data will be lost.`,
      async () => {
        setIsLoading(true);
        try {
          const success = await pwaDataManager.restoreFromBackup(backupVersion.id);
          if (success) {
            toast.success('Data restored successfully!');
            await loadData();
          } else {
            toast.error('Failed to restore data');
          }
        } catch (error) {
          console.error('Error restoring data:', error);
          toast.error('Failed to restore data');
        } finally {
          setIsLoading(false);
        }
        hideConfirmation();
      },
      'restore',
      backupVersion
    );
  };

  const handleDeleteBackup = (backupVersion: BackupVersion) => {
    showConfirmation(
      'Delete Backup',
      `Are you sure you want to delete the backup "${backupVersion.name}" created on ${backupVersion.timestamp.toLocaleString()}? This action cannot be undone and you will lose this backup permanently.`,
      async () => {
        setIsLoading(true);
        try {
          const success = await pwaDataManager.deleteBackup(backupVersion.id);
          if (success) {
            toast.success('Backup deleted successfully!');
            await loadBackupVersions();
          } else {
            toast.error('Failed to delete backup');
          }
        } catch (error) {
          console.error('Error deleting backup:', error);
          toast.error('Failed to delete backup');
        } finally {
          setIsLoading(false);
        }
        hideConfirmation();
      },
      'delete',
      backupVersion
    );
  };

  const getAppStatus = () => {
    const version = versionManager.getCurrentVersion();
    const build = versionManager.getCurrentBuild();
    
    if (isInstalled) {
      return { status: 'healthy', text: 'App Installed', details: `Running as installed app (v${version} Build ${build})` };
    } else if (installSupported) {
      return { status: 'warning', text: 'Install Available', details: `Can be installed for better experience (v${version} Build ${build})` };
    } else {
      return { status: 'error', text: 'Browser Mode', details: `Limited functionality in browser (v${version} Build ${build})` };
    }
  };

  const getStorageStatus = () => {
    if (!storageInfo) {
      return { status: 'warning', text: 'Storage Unknown', details: 'Unable to check storage usage' };
    }
    
    const usagePercent = storageInfo.quota ? (storageInfo.usage || 0) / storageInfo.quota : 0;
    
    if (usagePercent > 0.9) {
      return { status: 'error', text: 'Storage Full', details: `${Math.round(usagePercent * 100)}% used` };
    } else if (usagePercent > 0.7) {
      return { status: 'warning', text: 'Storage High', details: `${Math.round(usagePercent * 100)}% used` };
    } else {
      return { status: 'healthy', text: 'Storage OK', details: `${Math.round(usagePercent * 100)}% used` };
    }
  };

  const getPersistenceStatus = () => {
    if (isPersisted === true) {
      return { status: 'healthy', text: 'Data Protected', details: 'Data will persist across sessions' };
    } else if (isPersisted === false) {
      return { status: 'warning', text: 'Data at Risk', details: 'Data may be cleared by browser' };
    } else {
      return { status: 'warning', text: 'Persistence Unknown', details: 'Unable to check data protection' };
    }
  };

  if (!isOpen) return null;

  const appStatus = getAppStatus();
  const storageStatus = getStorageStatus();
  const persistenceStatus = getPersistenceStatus();

  return (
    <>
      <SettingsContainer $isOpen={isOpen}>
        <SettingsHeader>
          <SettingsTitle>
            <Settings size={20} />
            App Settings
          </SettingsTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </SettingsHeader>

        <SettingsContent>
          {/* Sync Status Section */}
          <Section>
            <SectionHeader onClick={() => toggleSection('sync')}>
              <SectionTitle>
                <RefreshCw size={18} />
                Sync Status
              </SectionTitle>
              {expandedSections.sync ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </SectionHeader>
            <SectionContent $isExpanded={expandedSections.sync}>
              <StatusItem $status={syncStatus.isOnline ? 'healthy' : 'error'}>
                <StatusIcon>
                  {syncStatus.isOnline ? <CheckCircle size={16} /> : <XCircle size={16} />}
                </StatusIcon>
                <StatusText>
                  {syncStatus.isOnline ? 'Online' : 'Offline'}
                  <StatusDetails>
                    {syncStatus.pendingActions > 0 
                      ? `${syncStatus.pendingActions} actions pending` 
                      : 'All synced'}
                  </StatusDetails>
                </StatusText>
              </StatusItem>
              
              <ActionButton 
                $variant="primary" 
                $size="sm" 
                onClick={handleForceSync}
                disabled={!syncStatus.isOnline || syncStatus.isSyncing}
              >
                <RefreshCw size={14} />
                {syncStatus.isSyncing ? 'Syncing...' : 'Force Sync'}
              </ActionButton>
            </SectionContent>
          </Section>

          {/* App Health Section */}
          <Section>
            <SectionHeader onClick={() => toggleSection('health')}>
              <SectionTitle>
                <Shield size={18} />
                App Health
              </SectionTitle>
              {expandedSections.health ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </SectionHeader>
            <SectionContent $isExpanded={expandedSections.health}>
              <StatusItem $status={appStatus.status}>
                <StatusIcon>
                  {appStatus.status === 'healthy' ? <CheckCircle size={16} /> :
                   appStatus.status === 'warning' ? <AlertTriangle size={16} /> :
                   <XCircle size={16} />}
                </StatusIcon>
                <StatusText>
                  {appStatus.text}
                  <StatusDetails>{appStatus.details}</StatusDetails>
                </StatusText>
              </StatusItem>

              <StatusItem $status={storageStatus.status}>
                <StatusIcon>
                  <HardDrive size={16} />
                </StatusIcon>
                <StatusText>
                  {storageStatus.text}
                  <StatusDetails>{storageStatus.details}</StatusDetails>
                </StatusText>
              </StatusItem>

              <StatusItem $status={persistenceStatus.status}>
                <StatusIcon>
                  <Database size={16} />
                </StatusIcon>
                <StatusText>
                  {persistenceStatus.text}
                  <StatusDetails>{persistenceStatus.details}</StatusDetails>
                </StatusText>
              </StatusItem>
            </SectionContent>
          </Section>

          {/* Data Backup Section */}
          <Section>
            <SectionHeader onClick={() => toggleSection('backup')}>
              <SectionTitle>
                <Database size={18} />
                Data Backup
              </SectionTitle>
              {expandedSections.backup ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </SectionHeader>
            <SectionContent $isExpanded={expandedSections.backup}>
              <ActionButton 
                $variant="primary" 
                $size="sm" 
                onClick={handleBackupData}
                disabled={isLoading}
              >
                <Download size={14} />
                Create Backup
              </ActionButton>

              {backupVersions.length > 0 && (
                <BackupList>
                  {backupVersions.map((backup) => (
                    <BackupItem key={backup.id}>
                      <BackupInfo>
                        <BackupName>{backup.name}</BackupName>
                        <BackupDate>
                          {backup.timestamp.toLocaleString()} • {(backup.size / 1024 / 1024).toFixed(1)}MB
                        </BackupDate>
                      </BackupInfo>
                      <BackupActions>
                        <BackupActionButton 
                          $variant="restore"
                          onClick={() => handleRestoreData(backup)}
                        >
                          Restore
                        </BackupActionButton>
                        <BackupActionButton 
                          $variant="delete"
                          onClick={() => handleDeleteBackup(backup)}
                        >
                          Delete
                        </BackupActionButton>
                      </BackupActions>
                    </BackupItem>
                  ))}
                </BackupList>
              )}
            </SectionContent>
          </Section>

          {/* Base URL Configuration Section */}
          <Section>
            <SectionHeader onClick={() => toggleSection('baseurl')}>
              <SectionTitle>
                <Globe size={18} />
                Base URL Configuration
              </SectionTitle>
              {expandedSections.baseurl ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </SectionHeader>
            <SectionContent $isExpanded={expandedSections.baseurl}>
              <BaseUrlConfig onUrlChange={(newUrl) => {
                console.log('Base URL changed:', newUrl);
                // Optionally refresh the page or update other components
              }} />
            </SectionContent>
          </Section>

          {/* App Installation Section */}
          <Section>
            <SectionHeader onClick={() => toggleSection('install')}>
              <SectionTitle>
                <Smartphone size={18} />
                App Installation
              </SectionTitle>
              {expandedSections.install ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </SectionHeader>
            <SectionContent $isExpanded={expandedSections.install}>
              <StatusItem $status={isInstalled ? 'healthy' : 'warning'}>
                <StatusIcon>
                  {isInstalled ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
                </StatusIcon>
                <StatusText>
                  {isInstalled ? 'App Installed' : 'Install Available'}
                  <StatusDetails>
                    {isInstalled 
                      ? 'Running as installed app' 
                      : `Install for better experience • User Engagement: ${userEngagement ? 'Active' : 'Required'} • Can Install: ${canInstall ? 'Yes' : 'No'}`
                    }
                  </StatusDetails>
                </StatusText>
              </StatusItem>

              {/* Installation Status Details */}
              <div style={{ 
                marginTop: '12px', 
                padding: '12px', 
                backgroundColor: '#f8fafc', 
                border: '1px solid #e2e8f0', 
                borderRadius: '6px',
                fontSize: '13px'
              }}>
                <div style={{ 
                  fontWeight: '600', 
                  color: '#374151', 
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <Info size={14} />
                  Installation Status
                </div>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '8px',
                  fontSize: '12px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Installed:</span>
                    <span style={{ 
                      color: isInstalled ? '#059669' : '#d97706',
                      fontWeight: '500'
                    }}>
                      {isInstalled ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Can Install:</span>
                    <span style={{ 
                      color: canInstall ? '#059669' : '#d97706',
                      fontWeight: '500'
                    }}>
                      {canInstall ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>User Engaged:</span>
                    <span style={{ 
                      color: userEngagement ? '#059669' : '#d97706',
                      fontWeight: '500'
                    }}>
                      {userEngagement ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Incognito:</span>
                    <span style={{ 
                      color: isIncognito ? '#d97706' : '#059669',
                      fontWeight: '500'
                    }}>
                      {isIncognito ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Mobile:</span>
                    <span style={{ 
                      color: isMobile ? '#059669' : '#6b7280',
                      fontWeight: '500'
                    }}>
                      {isMobile ? 'Yes' : 'No'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>iOS:</span>
                    <span style={{ 
                      color: isIOSDevice ? '#059669' : '#6b7280',
                      fontWeight: '500'
                    }}>
                      {isIOSDevice ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>

              {!isInstalled && !isIncognito && (
                <ActionButton 
                  $variant="primary" 
                  $size="sm" 
                  onClick={handleInstall}
                  style={{ marginBottom: '8px', marginRight: '8px' }}
                >
                  <Download size={14} />
                  Install App
                </ActionButton>
              )}

              {!isInstalled && !isIncognito && (
                <ActionButton 
                  $variant="secondary" 
                  $size="sm" 
                  onClick={handleForceInstall}
                  style={{ marginBottom: '8px' }}
                >
                  <Zap size={14} />
                  Force Install
                </ActionButton>
              )}

              {!isInstalled && isMobile && !isIncognito && (
                <ActionButton 
                  $variant="primary" 
                  $size="sm" 
                  onClick={handleMobileInstall}
                  style={{ marginBottom: '8px' }}
                >
                  <Smartphone size={14} />
                  Install on Mobile
                </ActionButton>
              )}

              {!isInstalled && isIncognito && (
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: '#fef3c7', 
                  border: '1px solid #f59e0b', 
                  borderRadius: '6px',
                  marginTop: '12px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    marginBottom: '8px',
                    color: '#92400e',
                    fontWeight: '500'
                  }}>
                    <AlertTriangle size={16} />
                    Incognito Mode Detected
                  </div>
                  <div style={{ 
                    fontSize: '13px', 
                    color: '#92400e',
                    lineHeight: '1.4',
                    marginBottom: '12px'
                  }}>
                    PWA installation may not be available in incognito/private mode. 
                    If you're in normal mode, try the install button below.
                  </div>
                  <ActionButton 
                    $variant="secondary" 
                    $size="sm" 
                    onClick={handleInstall}
                    style={{ width: '100%' }}
                  >
                    <Download size={14} />
                    Try Install Anyway
                  </ActionButton>
                </div>
              )}

              {!isInstalled && !canInstall && !isIncognito && (
                <div style={{ marginTop: '12px' }}>
                  <ActionButton 
                    $variant="primary" 
                    $size="sm" 
                    onClick={triggerInstallPrompt}
                    style={{ marginBottom: '8px', marginRight: '8px' }}
                  >
                    <RefreshCw size={14} />
                    Try Again
                  </ActionButton>
                  <ActionButton 
                    $variant="secondary" 
                    $size="sm" 
                    onClick={handleShowInstructions}
                    style={{ marginBottom: '8px', marginRight: '8px' }}
                  >
                    <ExternalLink size={14} />
                    Show Installation Instructions
                  </ActionButton>
                  <ActionButton 
                    $variant="secondary" 
                    $size="sm" 
                    onClick={handleDebugInstall}
                    style={{ marginBottom: '8px', marginRight: '8px' }}
                  >
                    <Info size={14} />
                    Debug Install
                  </ActionButton>
                  <ActionButton 
                    $variant="secondary" 
                    $size="sm" 
                    onClick={handleComprehensiveTest}
                    style={{ marginBottom: '8px', marginRight: '8px' }}
                  >
                    <Zap size={14} />
                    Full Test
                  </ActionButton>
                  <ActionButton 
                    $variant="secondary" 
                    $size="sm" 
                    onClick={handleQuickTest}
                    style={{ marginBottom: '8px', marginRight: '8px' }}
                  >
                    <RefreshCw size={14} />
                    Quick Test
                  </ActionButton>
                  <ActionButton 
                    $variant="secondary" 
                    $size="sm" 
                    onClick={handleDebugVersion}
                    style={{ marginBottom: '8px' }}
                  >
                    <Info size={14} />
                    Debug Version
                  </ActionButton>
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Quick guide:</strong>
                    </div>
                    {isIOSDevice && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                        <Smartphone size={14} />
                        <span>iOS Safari: Tap the share icon → "Add to Home Screen"</span>
                      </div>
                    )}
                    {isAndroidDevice && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                        <Smartphone size={14} />
                        <span>Android Chrome: Tap the three-dot menu → "Install app"</span>
                      </div>
                    )}
                    {!isMobile && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                        <Monitor size={14} />
                        <span>Desktop: Click the install icon in your browser's address bar</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </SectionContent>
          </Section>

          {/* App Updates Section */}
          <Section>
            <SectionHeader onClick={() => toggleSection('updates')}>
              <SectionTitle>
                <ArrowUpCircle size={18} />
                App Updates
              </SectionTitle>
              {expandedSections.updates ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </SectionHeader>
            <SectionContent $isExpanded={expandedSections.updates}>
              {updateInfo ? (
                <StatusItem $status="warning">
                  <StatusIcon>
                    {getUpdateIcon(updateInfo.updateType)}
                  </StatusIcon>
                  <StatusText>
                    {getUpdateTitle(updateInfo.updateType)}
                    <StatusDetails>
                      {getUpdateDescription(
                        updateInfo.updateType, 
                        updateInfo.latestVersion, 
                        updateInfo.latestBuild
                      )}
                    </StatusDetails>
                  </StatusText>
                </StatusItem>
              ) : (
                <StatusItem $status="healthy">
                  <StatusIcon>
                    <CheckCircle size={16} />
                  </StatusIcon>
                  <StatusText>
                    App Up to Date
                    <StatusDetails>
                      Version: {versionManager.getCurrentVersion()} (Build {versionManager.getCurrentBuild()}) • 
                      Environment: {process.env.NODE_ENV} • 
                      User Engagement: {userEngagement ? 'Active' : 'Required'}
                    </StatusDetails>
                  </StatusText>
                </StatusItem>
              )}

              {updateInfo && (
                <ActionButton 
                  $variant="primary" 
                  $size="sm" 
                  onClick={handleUpdate}
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <>
                      <RefreshCw size={14} className="animate-spin" />
                      Updating... {updateProgress}%
                    </>
                  ) : (
                    <>
                      <ArrowUpCircle size={14} />
                      Update Now
                    </>
                  )}
                </ActionButton>
              )}

              <ActionButton 
                $variant="secondary" 
                $size="sm" 
                onClick={checkForUpdates}
                disabled={isLoading}
                style={{ marginBottom: '8px' }}
              >
                <RefreshCw size={14} />
                Check for Updates
              </ActionButton>

              <ActionButton 
                $variant="secondary" 
                $size="sm" 
                onClick={handleForceRefresh}
                disabled={isLoading}
                style={{ marginBottom: '12px' }}
              >
                <RefreshCw size={14} />
                Force Refresh Build Files
              </ActionButton>

              {/* Enhanced Version Information */}
              <div style={{ 
                marginTop: '12px', 
                padding: '12px', 
                backgroundColor: '#f8fafc', 
                border: '1px solid #e2e8f0', 
                borderRadius: '6px',
                fontSize: '13px'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <span style={{ fontWeight: '500', color: '#374151' }}>Current Version:</span>
                  <span style={{ color: '#1f2937', fontFamily: 'monospace' }}>
                    {versionManager.getCurrentVersion()} (Build {versionManager.getCurrentBuild()})
                  </span>
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <span style={{ fontWeight: '500', color: '#374151' }}>Last Updated:</span>
                  <span style={{ color: '#6b7280' }}>
                    {new Date().toLocaleString()}
                  </span>
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <span style={{ fontWeight: '500', color: '#374151' }}>Environment:</span>
                  <span style={{ 
                    color: process.env.NODE_ENV === 'production' ? '#059669' : '#d97706',
                    fontWeight: '500'
                  }}>
                    {process.env.NODE_ENV === 'production' ? 'Production' : 'Development'}
                  </span>
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center'
                }}>
                  <span style={{ fontWeight: '500', color: '#374151' }}>User Engagement:</span>
                  <span style={{ 
                    color: userEngagement ? '#059669' : '#d97706',
                    fontWeight: '500'
                  }}>
                    {userEngagement ? 'Active' : 'Required'}
                  </span>
                </div>
              </div>
            </SectionContent>
          </Section>
        </SettingsContent>
      </SettingsContainer>

      {/* Confirmation Dialog */}
      <ConfirmationDialog $isVisible={confirmation.isVisible}>
        <ConfirmationContent>
          <ConfirmationTitle>{confirmation.title}</ConfirmationTitle>
          <ConfirmationMessage>{confirmation.message}</ConfirmationMessage>
          <ConfirmationButtons>
            <ConfirmationButton 
              $variant="secondary" 
              onClick={hideConfirmation}
            >
              Cancel
            </ConfirmationButton>
            <ConfirmationButton 
              $variant={confirmation.type === 'delete' ? 'danger' : 'primary'}
              onClick={confirmation.onConfirm}
            >
              {confirmation.type === 'backup' ? 'Create Backup' :
               confirmation.type === 'restore' ? 'Restore Data' :
               'Delete Backup'}
            </ConfirmationButton>
          </ConfirmationButtons>
        </ConfirmationContent>
      </ConfirmationDialog>

      {/* PWA Install Instructions */}
      <PWAInstallInstructions 
        isOpen={showInstallInstructions} 
        onClose={() => setShowInstallInstructions(false)} 
      />
    </>
  );
};

export default AppSettings;
